/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, type ReactNode, useCallback, useEffect } from 'react';

import { css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

const activeStyle = css({
	backgroundColor: token('color.background.information'),
	borderRadius: token('radius.xsmall'),
});

const enteredStyle = css({
	outlineOffset: token('space.075'),
	outlineWidth: token('border.width.selected'),
	outlineStyle: 'solid',
	outlineColor: token('color.border.selected'),
});

export type GroupDropIndicatorWithDragOverProps = {
	children: ReactNode;
	disableEntered?: boolean;
	isActive: boolean;
	testId?: string;
};

export const GroupDropIndicatorWithDragOver = forwardRef<
	HTMLDivElement,
	GroupDropIndicatorWithDragOverProps
>(function GroupDropIndicatorWithDragOver(
	{ children, isActive, testId, disableEntered },
	forwardedRef,
) {
	const [isEntered, setIsEntered] = React.useState(false);

	/**
	 * Manually handle drag over and leave events to apply border styles when drag and drop is active
	 */
	const handlePointerState = useCallback(
		(action: 'enter' | 'leave') => () => {
			!disableEntered && setIsEntered(action === 'enter');
		},
		[disableEntered],
	);

	/**
	 * Entered state should be reset when isActive is false from drag and drop context
	 */
	useEffect(() => {
		if (!isActive) {
			setIsEntered(false);
		}
	}, [isActive]);

	return (
		<div
			ref={forwardedRef}
			data-testid={testId}
			data-is-active={isActive}
			data-is-entered={isEntered}
			css={[isActive && activeStyle, isEntered && enteredStyle]}
			onDragOverCapture={isActive ? handlePointerState('enter') : undefined}
			onDragLeaveCapture={isActive ? handlePointerState('leave') : undefined}
		>
			{children}
		</div>
	);
});
